/**
 * Dimmer component - wrapper around web-palette Dimmer
 * A semi-transparent overlay that covers the entire viewport, typically used as a backdrop for modals and dialogs.
 *
 * @module views/components/Dimmer
 * @memberof -Common
 */
import React, { forwardRef } from 'react';

import PropTypes from 'prop-types';
import { Dimmer as PalDimmer } from 'web-palette/dist/components/Dimmer/Dimmer';

import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

/**
 * Represents a Dimmer component
 *
 * @method
 * @param {DimmerProps} props - React properties passed from composition
 * @param {React.Ref} ref - Forwarded ref to the underlying dimmer element
 * @returns Dimmer
 */
export const Dimmer = forwardRef( ( props, ref ) => {
  const { className, onClick, zIndex, ...rest } = handleEmptyObjects( props );

  return (
    <PalDimmer
      ref={ ref }
      { ...spreadProps( { className, onClick, zIndex, ...rest } ) }
    />
  );
} );

Dimmer.displayName = 'Dimmer';

/**
 * Property type definitions
 * @typedef DimmerProps
 * @type {object}
 * @property {string} className - Additional CSS class names to apply
 * @property {func} onClick - Callback function when dimmer is clicked
 * @property {number} zIndex - Sets the z-index of the Dimmer overlay
 */
export const propTypes = {
  /**
   * Additional CSS class names to apply
   */
  className: PropTypes.string,
  /**
   * Callback function when dimmer is clicked
   */
  onClick: PropTypes.func,
  /**
   * Sets the z-index of the Dimmer overlay
   */
  zIndex: PropTypes.number
};

Dimmer.propTypes = propTypes;

export default Dimmer;
